import React, { useState } from "react";
import { Loader2, HelpCircle, PartyPopper, Upload, QrCode } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400 text-[#FDFBF7]";
const QR = "https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&data=https://sirkingmaine.com/merch";
const FAQS = [
  { q: "How is Sir. King Kitchen merch made?", a: "Every item is made-to-order (print-on-demand) by our fulfillment partner — printed or embroidered fresh when you order, then shipped straight to you." },
  { q: "How long does shipping take?", a: "Because items are made-to-order, expect roughly 2–5 business days for production plus shipping transit. You'll get a tracking link by email as soon as it ships." },
  { q: "Can I return or exchange an item?", a: "Since items are custom-made, we accept returns/exchanges for wrong sizes or defects. Reach out within 14 days of delivery using the form below and we'll make it right." },
  { q: "My item arrived damaged or incorrect — what do I do?", a: "We're so sorry! Use the support form below, include your order number, and attach a photo of the issue. We'll arrange a replacement or refund." },
  { q: "How do I check my order status?", a: "Use Track Order (in the menu) with your order number, or sign in to your account to see order history and tracking." },
];

export default function MerchReturnsPage() {
  const [f, setF] = useState({ name: "", email: "", order_number: "", issue: "Damaged / incorrect item", message: "" });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Please use an image under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!f.name || !f.email || !f.message) { toast.error("Please add your name, email and a message."); return; }
    setBusy(true);
    try { await api.post("/merch/support", { ...f, photo }); setDone(true); }
    catch { toast.error("Could not send. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen max-w-3xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl">Merch Returns & <span className="text-gradient-gold">FAQ</span></h1>
      <p className="text-[#FDFBF7]/70 mt-3">Everything about how our gear is made, shipped, and how we take care of you.</p>

      <div className="mt-8 space-y-3">
        {FAQS.map((x) => (
          <div key={x.q} className="rounded-xl bg-[#181D1A] border border-emerald-900/60 p-4">
            <div className="flex items-start gap-2 font-display font-bold"><HelpCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" /> {x.q}</div>
            <p className="text-sm text-[#FDFBF7]/70 mt-2 pl-7">{x.a}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-2xl bg-emerald-950/40 border border-amber-500/30 p-4 flex flex-wrap items-center gap-4" data-testid="merch-qr">
        <img src={QR} alt="Scan to shop Sir. King merch" className="w-28 h-28 rounded-lg bg-white p-1" />
        <div>
          <div className="font-display font-black uppercase flex items-center gap-2"><QrCode className="w-4 h-4 text-amber-400" /> Scan to Shop</div>
          <p className="text-sm text-[#FDFBF7]/60 mt-1">Point your phone camera here to open the merch store. Add this QR to menus, delivery bags, flyers & business cards.</p>
        </div>
      </div>

      <div className="mt-10 rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-6" data-testid="merch-support-form">
        <h2 className="font-display font-black uppercase text-2xl text-amber-400">Merch Support</h2>
        {done ? (
          <div className="text-center py-8" data-testid="support-thank-you">
            <span className="grid place-items-center w-14 h-14 rounded-full bg-amber-400 text-[#0A2818] mx-auto"><PartyPopper className="w-7 h-7" /></span>
            <p className="font-display font-black uppercase text-xl mt-3">Message Sent!</p>
            <p className="text-[#FDFBF7]/70 text-sm mt-1">Thanks — our team will get back to you shortly.</p>
          </div>
        ) : (
          <div className="space-y-3 mt-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="support-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Name*" className={inp} />
              <input data-testid="support-email" value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="Email*" className={inp} />
              <input data-testid="support-order" value={f.order_number} onChange={(e) => set("order_number", e.target.value)} placeholder="Order number (if any)" className={inp} />
              <select data-testid="support-issue" value={f.issue} onChange={(e) => set("issue", e.target.value)} className={inp}>
                {["Damaged / incorrect item", "Wrong size", "Return / exchange", "Shipping question", "Other"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <textarea data-testid="support-message" value={f.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="How can we help?*" className={inp} />
            <label className="flex items-center gap-2 text-sm text-[#FDFBF7]/70 cursor-pointer">
              <Upload className="w-4 h-4 text-amber-400" /> {photo ? "Photo attached ✓ (tap to change)" : "Attach a photo (for damaged/incorrect items)"}
              <input data-testid="support-photo" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
            </label>
            {photo && <img src={photo} alt="attachment preview" className="w-24 h-24 object-cover rounded-lg" />}
            <button data-testid="support-submit" onClick={submit} disabled={busy} className="w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Send Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
